import React from 'react';
import { Layout, Avatar, Dropdown, Badge, Button, Input, Space, Switch, Tooltip } from 'antd';
import {
  BellOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useStore from '../../store/useStore';

const { Header: AntHeader } = Layout;
const { Search } = Input;

const Header = () => {
  const navigate = useNavigate();
  const { user, logout, themeMode, toggleTheme } = useStore();

  // Use Dropdown menu onClick to handle actions reliably in Ant Design v5
  const onUserMenuClick = ({ key }) => {
    switch (key) {
      case 'profile':
        navigate('/profile');
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'logout':
        navigate('/logout');
        break;
      default:
        break;
    }
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
    },
  ];

  const handleSearch = (value) => {
    if (value.trim()) {
      navigate(`/search?q=${encodeURIComponent(value)}`);
    }
  };

  return (
    <AntHeader className="px-6 flex items-center justify-between bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 shadow-md ring-1 ring-primary-100 dark:ring-slate-800 bg-gradient-to-r from-primary-100 to-transparent dark:from-slate-800">
      <div className="flex items-center gap-4 flex-1">
        <Search
          placeholder="Search products, orders, customers..."
          allowClear
          onSearch={handleSearch}
          style={{ maxWidth: 400 }}
          className="hidden md:block"
          enterButton={<SearchOutlined />}
          aria-label="Search inventory"
        />
      </div>

      <Space size="middle" className="flex items-center">
        {/* Mobile search button */}
        <Button
          type="text"
          icon={<SearchOutlined />}
          className="md:hidden"
          onClick={() => {
            // Could open a modal search on mobile
          }}
          aria-label="Open search"
        />

        {/* Notifications */}
        <Badge count={5} size="small">
          <Button
            type="text"
            icon={<BellOutlined />}
            className="flex items-center justify-center"
            onClick={() => navigate('/notifications')}
            aria-label="View notifications"
          />
        </Badge>

        {/* Theme toggle */}
        <Tooltip title={themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
          <Switch
            checked={themeMode === 'dark'}
            onChange={toggleTheme}
            checkedChildren={<span role="img" aria-label="dark">🌙</span>}
            unCheckedChildren={<span role="img" aria-label="light">☀️</span>}
            aria-label="Toggle dark mode"
          />
        </Tooltip>

        {/* User menu */}
        <Dropdown
          menu={{ items: userMenuItems, onClick: onUserMenuClick }}
          placement="bottomRight"
          trigger={['click']}
        >
          <div className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded-lg p-2 transition-colors">
            <Avatar
              size="small"
              icon={<UserOutlined />}
              src={user?.avatar}
              alt={user?.name || 'User avatar'}
            />
            <span className="hidden sm:block text-sm font-medium text-gray-700">
              {user?.name || 'User'}
            </span>
          </div>
        </Dropdown>
      </Space>
    </AntHeader>
  );
};

export default Header;
